import { Suspense, lazy, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, AudioLines, Bot, Gauge, History, IndianRupee, KeyRound, Link2, ListChecks, LogOut, ScrollText, Settings as SettingsIcon, UserRound, Users,
} from 'lucide-react';
import { api, type Me } from './api';
import { BrandMark, Loading } from './components/ui';
import SignIn from './pages/SignIn';
import Try from './pages/Try';
import MyHistory from './pages/MyHistory';
import ConversationView from './pages/ConversationView';
import Account from './pages/Account';
import LinkPage from './pages/LinkPage';
import MyProblems from './pages/MyProblems';
// Owner screens (and the chart library) load only for the owner, so the page a
// client opens stays small.
const Overview = lazy(() => import('./pages/owner/Overview'));
const Agents = lazy(() => import('./pages/owner/Agents'));
const AgentEdit = lazy(() => import('./pages/owner/AgentEdit'));
const Accounts = lazy(() => import('./pages/owner/Accounts'));
const Providers = lazy(() => import('./pages/owner/Providers'));
const Conversations = lazy(() => import('./pages/owner/Conversations'));
const SettingsPage = lazy(() => import('./pages/owner/Settings'));
const Audit = lazy(() => import('./pages/owner/Audit'));
const Teammates = lazy(() => import('./pages/owner/Teammates'));
const Plans = lazy(() => import('./pages/owner/Plans'));
const Problems = lazy(() => import('./pages/owner/Problems'));

/**
 * Three audiences, one app. The owner sees the full console; a demo account
 * (what you hand a client) sees the demo and nothing that costs or configures;
 * a prospect on a demo link (/d/<token>) signs in to nothing at all.
 */
export default function App() {
  const link = /^\/d\/([A-Za-z0-9_-]{10,40})\/?$/.exec(window.location.pathname);
  return link ? <LinkPage token={link[1]} /> : <Console />;
}

function Console() {
  const [me, setMe] = useState<Me | null>(null);
  const [appName, setAppName] = useState('AgeDash Voice');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api.get<{ appName: string }>('/api/auth/config').then((c) => {
      setAppName(c.appName);
      document.title = c.appName;
    }).catch(() => undefined);
    api
      .get<{ user: Me | null }>('/api/auth/me')
      .then((r) => setMe(r.user))
      .catch(() => setMe(null))
      .finally(() => setReady(true));
    const out = () => setMe(null);
    window.addEventListener('hv:signed-out', out);
    return () => window.removeEventListener('hv:signed-out', out);
  }, []);

  if (!ready) return <Loading />;
  if (!me) return <SignIn appName={appName} onSignedIn={setMe} />;
  return <Shell me={me} appName={appName} />;
}

function Shell({ me, appName }: { me: Me; appName: string }) {
  const owner = me.role === 'owner';
  const navigate = useNavigate();
  const link = (to: string, label: string, Icon: typeof Bot, end = false) => (
    <NavLink to={to} end={end} className={({ isActive }) => `nav ${isActive ? 'active' : ''}`}>
      <Icon aria-hidden="true" /> {label}
    </NavLink>
  );

  return (
    <div className="shell">
      <a className="skip" href="#main">Skip to content</a>
      <nav className="rail" aria-label="Main">
        <div className="brand">
          <BrandMark />
          {appName}
          {owner ? <small>owner</small> : me.teammateOf ? <small>teammate</small> : null}
        </div>

        <div className="rail-group">Demo</div>
        {link('/', 'Try the agent', AudioLines, true)}
        {link('/history', owner ? 'My test sessions' : 'My sessions', History)}
        {me.teammateOf ? link('/problems', 'Problems', AlertTriangle) : null}

        {owner ? (
          <>
            <div className="rail-group">Operate</div>
            {link('/owner/overview', 'Overview', Gauge)}
            {link('/owner/conversations', 'Conversations', Activity)}
            {link('/owner/problems', 'Problems', AlertTriangle)}
            <div className="rail-group">Build</div>
            {link('/owner/agents', 'Agents', Bot)}
            {link('/owner/plans', 'Plans & pricing', IndianRupee)}
            <div className="rail-group">Access</div>
            {link('/owner/accounts', 'Demo accounts', Users)}
            {link('/owner/teammates', 'Teammates & links', Link2)}
            <div className="rail-group">Setup</div>
            {link('/owner/providers', 'Providers & keys', KeyRound)}
            {link('/owner/settings', 'Settings', SettingsIcon)}
            {link('/owner/audit', 'Audit log', ScrollText)}
          </>
        ) : null}

        <div className="spacer" />
        <div className="rail-group" title={me.teammateOf ?? me.email}>{me.teammateOf ?? me.email}</div>
        {/* A teammate signs in from AgeDash, so there is no password here to change. */}
        {me.teammateOf ? null : link('/account', 'Account', owner ? ListChecks : UserRound)}
        <button
          className="btn sm"
          style={{ margin: '10px 8px 0', width: 'calc(100% - 16px)' }}
          onClick={async () => {
            await api.post('/api/auth/signout').catch(() => undefined);
            navigate('/');
            location.reload();
          }}
        >
          <LogOut aria-hidden="true" /> Sign out
        </button>
      </nav>

      <main className="main" id="main">
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Try me={me} />} />
          <Route path="/history" element={<MyHistory />} />
          <Route path="/conversations/:id" element={<ConversationView owner={owner} />} />
          <Route path="/account" element={<Account me={me} />} />
          {me.teammateOf ? <Route path="/problems" element={<MyProblems />} /> : null}
          {owner ? (
            <>
              <Route path="/owner/overview" element={<Overview />} />
              <Route path="/owner/conversations" element={<Conversations />} />
              <Route path="/owner/agents" element={<Agents />} />
              <Route path="/owner/agents/:id" element={<AgentEdit />} />
              <Route path="/owner/accounts" element={<Accounts me={me} />} />
              <Route path="/owner/providers" element={<Providers />} />
              <Route path="/owner/settings" element={<SettingsPage />} />
              <Route path="/owner/audit" element={<Audit />} />
              <Route path="/owner/teammates" element={<Teammates />} />
              <Route path="/owner/plans" element={<Plans />} />
              <Route path="/owner/problems" element={<Problems />} />
            </>
          ) : null}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
    </div>
  );
}
